import React, { useState, useEffect, useMemo } from 'react';
import {
    useForm,
    FormProvider,
    useFormContext,
    Controller,
} from 'react-hook-form';
import Drawer from '~/components/Drawer';
import {
    putCampaignRequestPatient,
    putCampaignRequestInitialContact,
    putCampaignApplicationPatient,
} from '~/services/campaign';
import Select from 'react-select';
import {
    inputStyles,
    labelStyles,
    requiredStyles,
    sectionStyles,
} from './styles';
import { GENDER_OPTIONS } from '~/constants/metadata';
import toast from 'react-hot-toast';

interface Props {
    isOpen: boolean;
    campaignRequest: any;
    setIsOpen: any;
    person: any;
    application?: any;
    states: any;
    medicalReasons?: any[];
    medicalIssues?: any[];
    injuryTimeFrames?: any[];
    insuranceTypes?: any[];
    transplantDiagnoses?: any[];
    candidacyStatuses?: any[];
    communityTypes?: any[];
    onPatientUpdate?: (apiResponse: any) => void;
}

type DefaultEditPatient = {
    title: string;
    suffix: string;
    first_name: string;
    middle_name: string;
    last_name: string;
    nicknames: string;
    update_photo: boolean;
    address_1: string;
    address_2: string;
    city: string;
    state: string;
    zip_code: string;
    gender: string;
    birth_date: string;
    patient_is_deceased: boolean;
    marital_status: string;
    ethnicity: string;
    preferred_language: string;
    email: string;
    phone: string;
    use_temporary_address: boolean;
    temporary_address_1: string;
    temporary_address_2: string;
    temporary_city: string;
    temporary_state: string;
    temporary_zip_code: string;
    temporary_country: string;
    temporary_from_date: string;
    temporary_to_date: string;
};

const defaultEditPatient: DefaultEditPatient = {
    title: '',
    suffix: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    nicknames: '',
    update_photo: false,
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    gender: '',
    birth_date: '',
    patient_is_deceased: false,
    marital_status: '',
    ethnicity: '',
    preferred_language: '',
    email: '',
    phone: '',
    use_temporary_address: false,
    temporary_address_1: '',
    temporary_address_2: '',
    temporary_city: '',
    temporary_state: '',
    temporary_zip_code: '',
    temporary_country: '',
    temporary_from_date: '',
    temporary_to_date: '',
};

const ErrorMessage = ({ message }: { message: string }) => (
    <p style={{ color: '#ff4b4b' }} className='mt-1 text-sm'>
        {message}
    </p>
);

// PersonDetails Component
const PersonDetails: React.FC<{ states: any }> = ({ states }) => {
    const {
        control,
        formState,
    } = useFormContext();
    const { errors } = formState;

    // Keep a live reference to dirtyFields so the phone validate closure reads
    // the current edit state. The strict format is enforced only when the user
    // has actually edited the phone — a pre-existing malformed number from a Web
    // CR should not block saving the rest of the form (HHLTB-2343).
    const dirtyFieldsRef = React.useRef(formState.dirtyFields);
    dirtyFieldsRef.current = formState.dirtyFields;

    const genderOptions = GENDER_OPTIONS.map((gender) => ({
        value: gender,
        label: gender,
    }));

    const formattedStates = (states || []).map((state: any) => ({
        value: state?.abbreviation,
        label: state?.state,
    }));

    return (
        <>
            <label htmlFor='cid-first-name' className={labelStyles}>
                First Name<span className={requiredStyles}>*</span>
            </label>
            <Controller
                name='person.first_name'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                    <input
                        id='cid-first-name'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />
            {errors.person?.first_name && (
                <ErrorMessage message='This field is required' />
            )}

            <label htmlFor='cid-middle-name' className={labelStyles}>
                Middle Name
            </label>
            <Controller
                name='person.middle_name'
                control={control}
                render={({ field }) => (
                    <input
                        id='cid-middle-name'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />

            <label htmlFor='cid-last-name' className={labelStyles}>
                Last Name<span className={requiredStyles}>*</span>
            </label>
            <Controller
                name='person.last_name'
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                    <input
                        id='cid-last-name'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />
            {errors.person?.last_name && (
                <ErrorMessage message='This field is required' />
            )}

            <label htmlFor='cid-nicknames' className={labelStyles}>
                Nickname
            </label>
            <Controller
                name='person.nicknames'
                control={control}
                render={({ field }) => (
                    <input
                        id='cid-nicknames'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />

            <label htmlFor='cid-email' className={labelStyles}>
                Email
            </label>
            <Controller
                name='person.email'
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                    <input
                        type='email'
                        id='cid-email'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />
            {errors.person?.email && (
                <ErrorMessage message='This field is required' />
            )}

            <label className={`w-full ${labelStyles}`}>Contact Number</label>
            <Controller
                name='person.phone'
                control={control}
                rules={{
                    validate: (value) => {
                        // Phone is optional; empty is always allowed.
                        if (!value || value.trim() === '') return true;

                        // Only enforce the strict format when the user actually
                        // edited the phone, so an untouched malformed value from
                        // a Web CR doesn't block the save (HHLTB-2343).
                        const phoneDirty = (
                            dirtyFieldsRef.current as { person?: { phone?: boolean } }
                        )?.person?.phone;
                        if (!phoneDirty) return true;

                        const pattern = /^\(\d{3}\) \d{3}-\d{4}$/;
                        if (!pattern.test(value)) {
                            return 'Please enter a valid phone number in format (123) 456-7890';
                        }
                        return true;
                    },
                }}
                render={({ field }) => (
                    <input
                        type='tel'
                        placeholder='(123) 456-7890'
                        maxLength={14}
                        onChange={(e) => {
                            // Remove all non-digits
                            const digits = e.target.value.replace(/\D/g, '');

                            // Format as (XXX) XXX-XXXX
                            let formatted = '';
                            if (digits.length >= 1) {
                                formatted = `(${digits.slice(0, 3)}`;
                            }
                            if (digits.length >= 4) {
                                formatted += `) ${digits.slice(3, 6)}`;
                            }
                            if (digits.length >= 7) {
                                formatted += `-${digits.slice(6, 10)}`;
                            }

                            field.onChange(formatted);
                        }}
                        value={field.value}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />
            {errors.person?.phone && (
                <ErrorMessage
                    message={String(
                        errors.person.phone.message || 'Invalid phone format',
                    )}
                />
            )}

            <label htmlFor='cid-address_1' className={labelStyles}>
                Street Address 1
            </label>
            <Controller
                name='person.address_1'
                control={control}
                render={({ field }) => (
                    <input
                        id='cid-address_1'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />

            <label htmlFor='cid-address_2' className={labelStyles}>
                Street Address 2
            </label>
            <Controller
                name='person.address_2'
                control={control}
                render={({ field }) => (
                    <input
                        id='cid-address_2'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />

            <label htmlFor='cid-city' className={labelStyles}>
                City
            </label>
            <Controller
                name='person.city'
                control={control}
                render={({ field }) => (
                    <input
                        id='cid-city'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />

            <label htmlFor='cid-state' className={labelStyles}>
                State
            </label>
            <Controller
                name='person.state'
                control={control}
                render={({ field }) => {
                    const selectedState = formattedStates.find(
                        (state: any) => state.value === field.value,
                    );
                    return (
                        <Select
                            menuPlacement='auto'
                            options={formattedStates}
                            className='w-full'
                            value={selectedState}
                            onChange={(option: any) =>
                                field.onChange(option?.value)
                            }
                        />
                    );
                }}
            />

            <label htmlFor='cid-zip_code' className={labelStyles}>
                Zip Code
            </label>
            <Controller
                name='person.zip_code'
                control={control}
                render={({ field }) => (
                    <input
                        id='cid-zip_code'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />

            <label htmlFor='cid-birth-date' className={labelStyles}>
                Date of Birth
            </label>
            <Controller
                name='person.birth_date'
                control={control}
                render={({ field }) => (
                    <input
                        type='date'
                        id='cid-birth-date'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />

            <label htmlFor='cid-gender' className={labelStyles}>
                Gender
            </label>
            <Controller
                name='person.gender'
                control={control}
                render={({ field }) => {
                    const selectedGender = genderOptions.find(
                        (option: any) => option.value === field.value,
                    );
                    return (
                        <Select
                            menuPlacement='auto'
                            id='cid-gender'
                            value={selectedGender}
                            onChange={(option: any) =>
                                field.onChange(option?.value)
                            }
                            options={genderOptions}
                            isClearable
                            className='w-full'
                        />
                    );
                }}
            />
        </>
    );
};

const MedicalInfoSection: React.FC<{
    medicalReasons: any[];
    medicalIssues: any[];
    injuryTimeFrames: any[];
    insuranceTypes: any[];
    transplantDiagnoses: any[];
    candidacyStatuses: any[];
    campaignRequest: any;
}> = ({
    medicalReasons,
    medicalIssues,
    injuryTimeFrames,
    insuranceTypes,
    transplantDiagnoses,
    candidacyStatuses,
    campaignRequest,
}) => {
    const {
        control,
        watch,
        setValue,
        formState: { errors },
    } = useFormContext();

    const [primaryMedicalReason, setPrimaryMedicalReason] = React.useState<
        number | null
    >(null);
    const [secondaryMedicalReason, setSecondaryMedicalReason] = React.useState<
        number | null
    >(null);

    const primaryMedicalIssue = watch('primary_medical_issue');
    const secondaryMedicalIssue = watch('secondary_medical_issue');
    const selectedInjuryTimeFrame = watch('injury_time_frame');

    // Initialize primary medical reason from campaignRequest on mount
    React.useEffect(() => {
        // First try to get from medical_reason field directly
        if (campaignRequest?.medical_reason) {
            const reasonId =
                typeof campaignRequest.medical_reason === 'object'
                    ? campaignRequest.medical_reason.id
                    : campaignRequest.medical_reason;
            setPrimaryMedicalReason(reasonId);
        }
        // Otherwise try to derive from primary_medical_issue
        else if (campaignRequest?.primary_medical_issue) {
            const issueId =
                typeof campaignRequest.primary_medical_issue === 'object'
                    ? campaignRequest.primary_medical_issue.id
                    : campaignRequest.primary_medical_issue;
            const issue = medicalIssues.find((mi: any) => mi.id === issueId);
            if (issue?.medical_reason) {
                const reasonId =
                    typeof issue.medical_reason === 'object'
                        ? issue.medical_reason.id
                        : issue.medical_reason;
                setPrimaryMedicalReason(reasonId);
            }
        }
    }, [campaignRequest, medicalIssues]);

    // Update primary medical reason when medical issue changes
    React.useEffect(() => {
        if (primaryMedicalIssue?.value) {
            const issue = medicalIssues.find(
                (mi: any) => mi.id === primaryMedicalIssue.value,
            );
            if (issue?.medical_reason) {
                const reasonId =
                    typeof issue.medical_reason === 'object'
                        ? issue.medical_reason.id
                        : issue.medical_reason;
                setPrimaryMedicalReason(reasonId);
            }
        }
    }, [primaryMedicalIssue, medicalIssues]);

    // Initialize secondary medical reason from campaignRequest on mount
    React.useEffect(() => {
        if (campaignRequest?.secondary_medical_issue) {
            const issueId =
                typeof campaignRequest.secondary_medical_issue === 'object'
                    ? campaignRequest.secondary_medical_issue.id
                    : campaignRequest.secondary_medical_issue;
            const issue = medicalIssues.find((mi: any) => mi.id === issueId);
            if (issue?.medical_reason) {
                const reasonId =
                    typeof issue.medical_reason === 'object'
                        ? issue.medical_reason.id
                        : issue.medical_reason;
                setSecondaryMedicalReason(reasonId);
            }
        }
    }, [campaignRequest, medicalIssues]);

    // Update secondary medical reason when medical issue changes
    React.useEffect(() => {
        if (secondaryMedicalIssue?.value) {
            const issue = medicalIssues.find(
                (mi: any) => mi.id === secondaryMedicalIssue.value,
            );
            if (issue?.medical_reason) {
                const reasonId =
                    typeof issue.medical_reason === 'object'
                        ? issue.medical_reason.id
                        : issue.medical_reason;
                setSecondaryMedicalReason(reasonId);
            }
        }
    }, [secondaryMedicalIssue, medicalIssues]);

    // Get filtered medical issues for primary based on selected reason
    const getPrimaryMedicalIssueOptions = (selectedReasonId: number | null) => {
        if (!selectedReasonId) return [];
        return medicalIssues
            .filter((issue: any) => {
                const issueReasonId =
                    typeof issue.medical_reason === 'object'
                        ? issue.medical_reason.id
                        : issue.medical_reason;
                return issueReasonId === selectedReasonId;
            })
            .map((issue: any) => ({
                value: issue.id,
                label: issue.name,
            }));
    };

    // Get filtered medical issues for secondary based on selected reason
    const getSecondaryMedicalIssueOptions = (
        selectedReasonId: number | null,
    ) => {
        if (!selectedReasonId) return [];
        return medicalIssues
            .filter((issue: any) => {
                const issueReasonId =
                    typeof issue.medical_reason === 'object'
                        ? issue.medical_reason.id
                        : issue.medical_reason;
                return issueReasonId === selectedReasonId;
            })
            .map((issue: any) => ({
                value: issue.id,
                label: issue.name,
            }));
    };

    const insuranceOptions = (insuranceTypes || []).map((type: any) => ({
        value: type?.id,
        label: type?.name,
    }));

    // Timeframe codes repeat across medical reasons (e.g. "within_1" exists
    // for Injury and Illness), so a timeframe from another reason maps to the
    // same code under the selected reason. Returns null when there is no
    // equivalent and undefined when the timeframe isn't in the lookup list.
    const getEquivalentTimeFrame = React.useCallback(
        (timeFrameId: number | undefined, reasonId: number | null) => {
            const getReasonId = (item: any) =>
                typeof item?.medical_reason === 'object'
                    ? item?.medical_reason?.id
                    : item?.medical_reason;
            const current = (injuryTimeFrames || []).find(
                (item: any) => item?.id === timeFrameId,
            );
            if (!current || !reasonId) {
                return undefined;
            }
            if (getReasonId(current) === reasonId) {
                return current;
            }
            return (
                (injuryTimeFrames || []).find(
                    (item: any) =>
                        getReasonId(item) === reasonId &&
                        item?.code === current.code,
                ) || null
            );
        },
        [injuryTimeFrames],
    );

    // Move a timeframe saved under another reason onto the selected reason.
    // Never clear it here: this runs when the drawer opens, and clearing
    // erased saved timeframes on the next save (HHLTB-2926).
    useEffect(() => {
        const equivalent = getEquivalentTimeFrame(
            selectedInjuryTimeFrame?.value,
            primaryMedicalReason,
        );
        if (equivalent && equivalent.id !== selectedInjuryTimeFrame?.value) {
            setValue('injury_time_frame', {
                value: equivalent.id,
                label: equivalent.name,
            });
        }
    }, [
        getEquivalentTimeFrame,
        selectedInjuryTimeFrame,
        primaryMedicalReason,
        setValue,
    ]);

    const injuryOptions = (injuryTimeFrames || [])
        .filter((frame: any) => {
            const frameReason =
                typeof frame?.medical_reason === 'object'
                    ? frame?.medical_reason?.id
                    : frame?.medical_reason;
            if (!primaryMedicalReason) {
                return true;
            }
            return frameReason === primaryMedicalReason;
        })
        .map((frame: any) => ({
            value: frame?.id,
            label: frame?.name,
        }));

    // Medical reason button styles
    const medicalReasonButtonBase =
        'rounded-full border-[2px] px-[24px] py-[10px] text-[12px]/[16px] font-bold uppercase transition-colors duration-300';
    const medicalReasonButtonActive = 'border-black bg-black text-white';
    const medicalReasonButtonInactive =
        'border-stroke bg-white text-black hover:border-gray-400';

    const handlePrimaryMedicalReasonChange = (reasonId: number) => {
        setPrimaryMedicalReason(reasonId);
        // Clear medical issue selection when reason changes
        // Don't validate immediately to avoid showing error before user selects
        setValue('primary_medical_issue', null, { shouldValidate: false });
        // The timeframe only clears when the user picks a reason that has
        // no equivalent option
        if (
            getEquivalentTimeFrame(selectedInjuryTimeFrame?.value, reasonId) ===
            null
        ) {
            setValue('injury_time_frame', null);
        }
    };

    const handleSecondaryMedicalReasonChange = (reasonId: number) => {
        setSecondaryMedicalReason(reasonId);
        // Clear medical issue selection when reason changes
        setValue('secondary_medical_issue', null, { shouldValidate: false });
    };

    const getMedicalReasonButton = (
        reasonId: number,
        currentReasonId: number | null,
        label: string,
        onClick: (reasonId: number) => void,
    ) => {
        const isActive = currentReasonId === reasonId;
        return (
            <button
                type='button'
                key={reasonId}
                className={`${medicalReasonButtonBase} ${
                    isActive
                        ? medicalReasonButtonActive
                        : medicalReasonButtonInactive
                }`}
                onClick={() => onClick(reasonId)}
            >
                {label}
            </button>
        );
    };

    return (
        <>
            {/* PRIMARY MEDICAL REASON */}
            <div className='mb-4 mt-4'>
                <h3 className='mb-3 text-[14px] font-semibold uppercase tracking-wide text-secondary'>
                    Primary Medical Reason
                </h3>
            </div>

            <label className={labelStyles}>Primary Medical Reason</label>
            <div className='mb-4 flex flex-wrap gap-2'>
                {medicalReasons.map((reason: any) =>
                    getMedicalReasonButton(
                        reason.id,
                        primaryMedicalReason,
                        reason.name,
                        handlePrimaryMedicalReasonChange,
                    ),
                )}
            </div>

            <label htmlFor='cid-primary-medical-issue' className={labelStyles}>
                {primaryMedicalReason
                    ? `Select Type of ${
                          medicalReasons.find(
                              (r: any) => r.id === primaryMedicalReason,
                          )?.name || 'Medical Issue'
                      }`
                    : 'Select Type of Medical Issue'}
            </label>
            <Controller
                name='primary_medical_issue'
                control={control}
                render={({ field }) => (
                    <Select
                        menuPlacement='auto'
                        id='cid-primary-medical-issue'
                        {...field}
                        options={getPrimaryMedicalIssueOptions(
                            primaryMedicalReason,
                        )}
                        isClearable
                        className='w-full'
                        placeholder='Select'
                        isDisabled={!primaryMedicalReason}
                    />
                )}
            />

            <label htmlFor='cid-medical-issue-notes' className={labelStyles}>
                Notes
            </label>
            <Controller
                name='medical_issue_notes'
                control={control}
                render={({ field }) => (
                    <textarea
                        id='cid-medical-issue-notes'
                        {...field}
                        rows={4}
                        placeholder='Text'
                        className={`${inputStyles} w-full resize-none`}
                    />
                )}
            />

            <label htmlFor='cid-injury-timeframe' className={labelStyles}>
                {primaryMedicalReason
                    ? `${
                          medicalReasons.find(
                              (r: any) => r.id === primaryMedicalReason,
                          )?.name || 'Medical Issue'
                      } Timeframe`
                    : 'Timeframe'}
            </label>
            <Controller
                name='injury_time_frame'
                control={control}
                render={({ field }) => (
                    <Select
                        menuPlacement='auto'
                        id='cid-injury-timeframe'
                        {...field}
                        options={injuryOptions}
                        isClearable
                        className='w-full'
                    />
                )}
            />

            <label htmlFor='cid-medical-issue-date' className={labelStyles}>
                {primaryMedicalReason
                    ? `${
                          medicalReasons.find(
                              (r: any) => r.id === primaryMedicalReason,
                          )?.name || 'Medical Issue'
                      } Date`
                    : 'Medical Issue Date'}
            </label>
            <Controller
                name='medical_issue_date'
                control={control}
                render={({ field }) => (
                    <input
                        id='cid-medical-issue-date'
                        type='date'
                        {...field}
                        className={`${inputStyles} w-full`}
                    />
                )}
            />

            {/* TRANSPLANT-SPECIFIC FIELDS */}
            {primaryMedicalReason &&
                medicalReasons.find((r: any) => r.id === primaryMedicalReason)
                    ?.code === 'transplant' && (
                    <>
                        <label
                            htmlFor='cid-transplant-diagnosis'
                            className={labelStyles}
                        >
                            Transplant Diagnosis
                        </label>
                        <Controller
                            name='transplant_diagnosis'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    menuPlacement='auto'
                                    id='cid-transplant-diagnosis'
                                    {...field}
                                    options={(transplantDiagnoses || []).map(
                                        (td: any) => ({
                                            value: td.id,
                                            label: td.name,
                                        }),
                                    )}
                                    isClearable
                                    className='w-full'
                                    placeholder='Select Transplant Diagnosis'
                                />
                            )}
                        />

                        <label
                            htmlFor='cid-candidacy-status'
                            className={labelStyles}
                        >
                            Candidacy Status
                        </label>
                        <Controller
                            name='candidacy_status'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    menuPlacement='auto'
                                    id='cid-candidacy-status'
                                    {...field}
                                    options={(candidacyStatuses || []).map(
                                        (cs: any) => ({
                                            value: cs.id,
                                            label: cs.name,
                                        }),
                                    )}
                                    isClearable
                                    className='w-full'
                                    placeholder='Select Candidacy Status'
                                />
                            )}
                        />
                    </>
                )}

            {/* SECONDARY MEDICAL REASON */}
            <div className='mb-4 mt-6'>
                <h3 className='mb-3 text-[14px] font-semibold uppercase tracking-wide text-secondary'>
                    Secondary Medical Reason
                </h3>
            </div>

            <label className={labelStyles}>Secondary Medical Reason</label>
            <div className='mb-4 flex flex-wrap gap-2'>
                {medicalReasons.map((reason: any) =>
                    getMedicalReasonButton(
                        reason.id,
                        secondaryMedicalReason,
                        reason.name,
                        handleSecondaryMedicalReasonChange,
                    ),
                )}
            </div>

            <label
                htmlFor='cid-secondary-medical-issue'
                className={labelStyles}
            >
                {secondaryMedicalReason
                    ? `Select Type of ${
                          medicalReasons.find(
                              (r: any) => r.id === secondaryMedicalReason,
                          )?.name || 'Medical Issue'
                      }`
                    : 'Select Type of Medical Issue'}
            </label>
            <Controller
                name='secondary_medical_issue'
                control={control}
                render={({ field }) => (
                    <Select
                        menuPlacement='auto'
                        id='cid-secondary-medical-issue'
                        {...field}
                        options={getSecondaryMedicalIssueOptions(
                            secondaryMedicalReason,
                        )}
                        isClearable
                        className='w-full'
                        placeholder='Select'
                        isDisabled={!secondaryMedicalReason}
                    />
                )}
            />

            {/* INSURANCE */}
            <div className='mb-4 mt-6'>
                <h3 className='mb-3 text-[14px] font-semibold uppercase tracking-wide text-secondary'>
                    Insurance
                </h3>
            </div>

            <label htmlFor='cid-insurance-type' className={labelStyles}>
                Insurance
            </label>
            <Controller
                name='insurance_type'
                control={control}
                render={({ field }) => (
                    <Select
                        menuPlacement='auto'
                        id='cid-insurance-type'
                        {...field}
                        options={insuranceOptions}
                        isClearable
                        className='w-full'
                    />
                )}
            />
        </>
    );
};

const CampaignRequestClientInfoDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    person,
    application,
    states,
    medicalReasons = [],
    medicalIssues = [],
    injuryTimeFrames = [],
    insuranceTypes = [],
    transplantDiagnoses = [],
    candidacyStatuses = [],
    communityTypes = [],
    campaignRequest,
    onPatientUpdate,
}) => {
    const normalizeStateValue = (stateValue: any): string => {
        if (!stateValue) return '';
        if (typeof stateValue === 'string') return stateValue;
        if (typeof stateValue === 'object') {
            return (
                stateValue?.abbreviation ||
                stateValue?.state ||
                stateValue?.st ||
                ''
            );
        }
        return '';
    };

    const toOption = (item: any) => {
        if (item === null || item === undefined) {
            return null;
        }

        if (typeof item === 'string' || typeof item === 'number') {
            return { value: item, label: String(item) };
        }

        const value = item?.id ?? item?.value ?? null;
        if (value === null || value === undefined) {
            return null;
        }

        const label = item?.name ?? item?.label ?? String(value);
        return { value, label };
    };

    const transformPersonForForm = useMemo(() => {
        const basePerson = person || {};
        const requestPatient = campaignRequest || {};

        // Check if this campaign request is for 'self' (initial contact is the patient)
        const isCampaignForSelf =
            campaignRequest?.campaign_is_for?.code === 'self';

        const pick = (primary: any, fallback: any) => {
            if (primary !== undefined && primary !== null && primary !== '') {
                return primary;
            }
            if (fallback !== undefined && fallback !== null) {
                return fallback;
            }
            return '';
        };

        // Normalize a stored phone for pre-fill: format only when the value
        // resolves to exactly 10 digits (optionally with a leading US country
        // code); otherwise return the original value unchanged so non-standard
        // numbers are never truncated or mangled into an invalid partial
        // (HHLTB-2343).
        const formatPhoneNumber = (phone: string): string => {
            if (!phone) return '';

            let digits = phone.replace(/\D/g, '');
            if (digits.length === 11 && digits.startsWith('1')) {
                digits = digits.slice(1);
            }
            if (digits.length === 10) {
                return `(${digits.slice(0, 3)}) ${digits.slice(
                    3,
                    6,
                )}-${digits.slice(6, 10)}`;
            }
            return phone;
        };

        // Format date for HTML date input (expects YYYY-MM-DD)
        // Treat dates as calendar dates without timezone conversion
        const formatDateForInput = (dateValue: any): string => {
            if (!dateValue) return '';
            try {
                const dateStr = String(dateValue);

                // If it's already in YYYY-MM-DD format, return as-is (no conversion)
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                    return dateStr;
                }

                // If it's an ISO string with time, extract just the date part
                const isoMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
                if (isoMatch) {
                    return isoMatch[1];
                }

                return '';
            } catch {
                return '';
            }
        };

        const resolvedState =
            normalizeStateValue(basePerson?.state) ||
            normalizeStateValue(requestPatient?.patient_state);

        // For 'self' campaigns, prioritize initial contact data, then patient data, then person data
        if (isCampaignForSelf) {
            return {
                ...defaultEditPatient,
                ...basePerson,
                first_name: pick(
                    requestPatient?.initial_first_name,
                    pick(
                        requestPatient?.patient_first_name,
                        basePerson?.first_name,
                    ),
                ),
                middle_name: pick(
                    requestPatient?.patient_middle_name,
                    basePerson?.middle_name,
                ),
                last_name: pick(
                    requestPatient?.initial_last_name,
                    pick(
                        requestPatient?.patient_last_name,
                        basePerson?.last_name,
                    ),
                ),
                nicknames: pick(
                    basePerson?.nicknames,
                    requestPatient?.patient_nicknames,
                ),
                address_1: pick(
                    basePerson?.address_1,
                    requestPatient?.patient_address_1,
                ),
                address_2: pick(
                    basePerson?.address_2,
                    requestPatient?.patient_address_2,
                ),
                city: pick(basePerson?.city, requestPatient?.patient_city),
                state: resolvedState,
                temporary_state: normalizeStateValue(
                    basePerson?.temporary_state,
                ),
                zip_code: pick(
                    basePerson?.zip_code,
                    requestPatient?.patient_zip_code,
                ),
                email: pick(
                    requestPatient?.initial_email,
                    pick(requestPatient?.patient_email, basePerson?.email),
                ),
                phone: formatPhoneNumber(
                    pick(
                        requestPatient?.initial_phone,
                        pick(requestPatient?.patient_phone1, basePerson?.phone),
                    ),
                ),
                gender: pick(
                    requestPatient?.patient_gender,
                    basePerson?.gender,
                ),
                birth_date: formatDateForInput(
                    pick(
                        requestPatient?.patient_birth_date,
                        basePerson?.birth_date,
                    ),
                ),
            };
        }

        // For non-'self' campaigns, use the original logic
        return {
            ...defaultEditPatient,
            ...basePerson,
            first_name: pick(
                basePerson?.first_name,
                requestPatient?.patient_first_name,
            ),
            middle_name: pick(
                basePerson?.middle_name,
                requestPatient?.patient_middle_name,
            ),
            last_name: pick(
                basePerson?.last_name,
                requestPatient?.patient_last_name,
            ),
            nicknames: pick(
                basePerson?.nicknames,
                requestPatient?.patient_nicknames,
            ),
            address_1: pick(
                basePerson?.address_1,
                requestPatient?.patient_address_1,
            ),
            address_2: pick(
                basePerson?.address_2,
                requestPatient?.patient_address_2,
            ),
            city: pick(basePerson?.city, requestPatient?.patient_city),
            state: resolvedState,
            temporary_state: normalizeStateValue(basePerson?.temporary_state),
            zip_code: pick(
                basePerson?.zip_code,
                requestPatient?.patient_zip_code,
            ),
            email: pick(basePerson?.email, requestPatient?.patient_email),
            phone: formatPhoneNumber(
                pick(basePerson?.phone, requestPatient?.patient_phone1),
            ),
            gender: pick(basePerson?.gender, requestPatient?.patient_gender),
            birth_date: formatDateForInput(
                pick(
                    basePerson?.birth_date,
                    requestPatient?.patient_birth_date,
                ),
            ),
        };
    }, [person, campaignRequest]);

    const transformCampaignRequestFields = useMemo(() => {
        return {
            primary_medical_issue: toOption(
                campaignRequest?.primary_medical_issue,
            ),
            secondary_medical_issue: toOption(
                campaignRequest?.secondary_medical_issue,
            ),
            medical_issue_notes: campaignRequest?.medical_issue_notes || '',
            medical_issue_date: campaignRequest?.medical_issue_date || '',
            injury_time_frame: toOption(campaignRequest?.injury_time_frame),
            insurance_type: toOption(campaignRequest?.insurance_type),
            transplant_diagnosis: toOption(
                campaignRequest?.transplant_diagnosis,
            ),
            candidacy_status: toOption(campaignRequest?.candidacy_status),
            community_types: (campaignRequest?.community_types || []).map(
                (ct: any) => ({ value: ct.id, label: ct.name }),
            ),
        };
    }, [campaignRequest]);

    const initialValues = useMemo(
        () => ({
            person: transformPersonForForm,
            ...transformCampaignRequestFields,
        }),
        [transformPersonForForm, transformCampaignRequestFields],
    );

    const methods = useForm({
        defaultValues: initialValues,
    });
    const { reset } = methods;

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        reset(initialValues);
    }, [initialValues, reset, isOpen]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClose = () => {
        if (
            methods.formState.isDirty &&
            !window.confirm(
                'You have unsaved changes. Are you sure you want to close?',
            )
        ) {
            return;
        }
        setIsOpen(false);
    };

    const onSubmit = async (data: any) => {
        setIsSubmitting(true);
        try {
            const personData = data?.person || {};
            const isCampaignForSelf =
                campaignRequest?.campaign_is_for?.code === 'self';

            const patientPayload = {
                first_name: personData?.first_name ?? '',
                middle_name: personData?.middle_name ?? '',
                last_name: personData?.last_name ?? '',
                nicknames: personData?.nicknames ?? '',
                address_1: personData?.address_1 ?? '',
                address_2: personData?.address_2 ?? '',
                city: personData?.city ?? '',
                state: personData?.state ?? '',
                zip_code: personData?.zip_code ?? '',
                email: personData?.email ?? '',
                phone: personData?.phone ?? '',
                // Convert empty string to null for date fields to avoid Django validation errors
                birth_date: personData?.birth_date?.trim() || null,
                primary_medical_issue_id:
                    data?.primary_medical_issue?.value ?? null,
                secondary_medical_issue_id:
                    data?.secondary_medical_issue?.value ?? null,
                medical_issue_notes: data?.medical_issue_notes ?? '',
                medical_issue_date: data?.medical_issue_date?.trim() || null,
                injury_time_frame_id: data?.injury_time_frame?.value ?? null,
                insurance_type_id: data?.insurance_type?.value ?? null,
                transplant_diagnosis_id:
                    data?.transplant_diagnosis?.value ?? null,
                candidacy_status_id: data?.candidacy_status?.value ?? null,
                gender: personData?.gender ?? '',
            };

            let apiResponse;

            // If an application exists, use the application endpoint
            // Otherwise, use the campaign request endpoint (for unapproved requests)
            const communityPayload = {
                request_source: campaignRequest?.request_source || '',
                campaign_is_for_id:
                    campaignRequest?.campaign_is_for?.id ?? null,
                heard_of_source_id:
                    campaignRequest?.heard_of_source?.id ?? null,
                community_types: (data?.community_types || []).map(
                    (ct: any) => ct.value,
                ),
            };

            if (application?.id) {
                // Campaign is approved and has an application - use application endpoint
                const [appResponse] = await Promise.all([
                    putCampaignApplicationPatient(
                        application.id,
                        patientPayload,
                    ),
                    putCampaignRequestInitialContact(
                        campaignRequest?.id,
                        communityPayload,
                    ),
                ]);
                apiResponse = appResponse;
            } else if (isCampaignForSelf) {
                // For 'self' campaigns (not yet approved), update both initial contact and patient data
                const initialContactPayload = {
                    ...communityPayload,
                    initial_first_name: personData?.first_name ?? '',
                    initial_last_name: personData?.last_name ?? '',
                    initial_email: personData?.email ?? '',
                    initial_phone: personData?.phone ?? '',
                };

                const [, patientResponse] = await Promise.all([
                    putCampaignRequestInitialContact(
                        campaignRequest?.id,
                        initialContactPayload,
                    ),
                    putCampaignRequestPatient(
                        campaignRequest?.id,
                        patientPayload,
                    ),
                ]);

                apiResponse = patientResponse;
            } else {
                // For non-'self' campaigns (not yet approved), update patient data and community types
                const [patientResponse] = await Promise.all([
                    putCampaignRequestPatient(
                        campaignRequest?.id,
                        patientPayload,
                    ),
                    putCampaignRequestInitialContact(
                        campaignRequest?.id,
                        communityPayload,
                    ),
                ]);
                apiResponse = patientResponse;
            }

            // Update parent component state with the API response
            if (onPatientUpdate) {
                onPatientUpdate(apiResponse);
            }

            toast.success('Client information saved successfully');
            setIsOpen(false);
        } catch (error: any) {
            console.error('Error submitting form:', error);

            // Extract error message from API response
            let errorMessage =
                'Failed to save client information. Please try again.';

            // Check for error message in various formats
            if (error?.detail) {
                // API service attaches detail property
                errorMessage = error.detail;
            } else if (error?.message) {
                // Plain Error object with message
                errorMessage = error.message;
            } else if (error?.response?.data) {
                // Axios-style error (for other API calls)
                const data = error.response.data;
                if (typeof data === 'string') {
                    errorMessage = data;
                } else if (data.error) {
                    errorMessage = data.error;
                } else if (data.detail) {
                    errorMessage = data.detail;
                } else if (typeof data === 'object') {
                    // Handle field-specific errors
                    const firstError = Object.values(data)[0];
                    if (Array.isArray(firstError)) {
                        errorMessage = firstError[0];
                    } else if (typeof firstError === 'string') {
                        errorMessage = firstError;
                    }
                }
            }

            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onError = (errors: any) => {
        console.error('Form validation errors:', errors);

        // Helper to extract error message from nested errors
        const getFirstErrorMessage = (
            errorObj: any,
            path = '',
        ): string | null => {
            if (!errorObj || typeof errorObj !== 'object') {
                return null;
            }

            // React Hook Form field errors are leaf objects shaped like
            // { type, message, ref }. Never recurse into them: `ref` is a DOM
            // node with circular references and `type` is a string, either of
            // which sends this function into infinite recursion.
            if (typeof errorObj.type === 'string' || 'ref' in errorObj) {
                const message =
                    typeof errorObj.message === 'string' && errorObj.message
                        ? errorObj.message
                        : 'This field is required';
                const fieldName = path.includes('person.first_name')
                    ? 'First Name'
                    : path.includes('person.last_name')
                    ? 'Last Name'
                    : path.includes('primary_medical_issue')
                    ? 'Primary Medical Issue'
                    : path || 'Field';
                return `${fieldName}: ${message}`;
            }

            for (const key in errorObj) {
                if (Object.prototype.hasOwnProperty.call(errorObj, key)) {
                    const newPath = path ? `${path}.${key}` : key;
                    const result = getFirstErrorMessage(errorObj[key], newPath);
                    if (result) {
                        return result;
                    }
                }
            }
            return null;
        };

        const errorMessage =
            getFirstErrorMessage(errors) ||
            'Please fill in all required fields';
        toast.error(errorMessage);
    };

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            onClose={handleClose}
            title='Client Info'
        >
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
                    <h2 className={sectionStyles}>CONTACT</h2>
                    <PersonDetails states={states} />

                    <h2 className={sectionStyles}>MEDICAL INFO</h2>
                    <MedicalInfoSection
                        medicalReasons={medicalReasons}
                        medicalIssues={medicalIssues}
                        injuryTimeFrames={injuryTimeFrames}
                        insuranceTypes={insuranceTypes}
                        transplantDiagnoses={transplantDiagnoses}
                        candidacyStatuses={candidacyStatuses}
                        campaignRequest={campaignRequest}
                    />

                    <h2 className={sectionStyles}>COMMUNITY</h2>
                    <label className={labelStyles}>Community</label>
                    <Controller
                        name='community_types'
                        control={methods.control}
                        render={({ field }) => (
                            <div className='flex flex-col gap-2'>
                                {(communityTypes || []).map((ct: any) => {
                                    const isChecked = (field.value || []).some(
                                        (v: any) => v.value === ct.id,
                                    );
                                    return (
                                        <label
                                            key={ct.id}
                                            className='flex cursor-pointer items-center gap-2 text-sm'
                                        >
                                            <input
                                                type='checkbox'
                                                checked={isChecked}
                                                onChange={() => {
                                                    const current =
                                                        field.value || [];
                                                    if (isChecked) {
                                                        field.onChange(
                                                            current.filter(
                                                                (v: any) =>
                                                                    v.value !==
                                                                    ct.id,
                                                            ),
                                                        );
                                                    } else {
                                                        field.onChange([
                                                            ...current,
                                                            {
                                                                value: ct.id,
                                                                label: ct.name,
                                                            },
                                                        ]);
                                                    }
                                                }}
                                            />
                                            {ct.name}
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    />

                    <div className='mb-[30px] flex gap-[16px]'>
                        <button
                            type='button'
                            className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            onClick={handleClose}
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <span className='flex items-center justify-center'>
                                    <svg
                                        className='mr-3 h-5 w-5 animate-spin'
                                        viewBox='0 0 24 24'
                                    >
                                        <circle
                                            className='opacity-25'
                                            cx='12'
                                            cy='12'
                                            r='10'
                                            stroke='currentColor'
                                            strokeWidth='4'
                                        />
                                        <path
                                            className='opacity-75'
                                            fill='currentColor'
                                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                        />
                                    </svg>
                                    Saving...
                                </span>
                            ) : (
                                'Save'
                            )}
                        </button>
                    </div>
                </form>
            </FormProvider>
        </Drawer>
    );
};

export default CampaignRequestClientInfoDrawer;
